import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { authService } from '../../../services/authService';

interface PasswordChangeFormProps {
  onPasswordChanged: () => void;
}

const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({ onPasswordChanged }) => {
  const uiData = useSelector((state: RootState) => state.ui);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Password validation
  const passwordRequirements = {
    minLength: formData.newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.newPassword),
    hasLowercase: /[a-z]/.test(formData.newPassword),
    hasNumber: /[0-9]/.test(formData.newPassword),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword)
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  const passwordsMatch = formData.newPassword === formData.confirmPassword;
  const canSubmit = formData.currentPassword && isPasswordValid && passwordsMatch && !loading;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const result = await authService.updatePassword(
        formData.currentPassword,
        formData.newPassword
      );

      if (result.success) {
        setSuccess(true);
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        onPasswordChanged();
      } else {
        setError(result.error || 'Failed to update password');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5vh',
      fontSize: '1.3vh',
      fontFamily: 'Noto Sans',
      color: met ? 'rgb(100, 200, 100)' : 'rgba(255, 255, 255, 0.5)'
    }}>
      <span>{met ? '✓' : '×'}</span>
      <span>{text}</span>
    </div>
  );

  return (
    <div style={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '2vh'
    }}>
      <form onSubmit={handleSubmit} style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2vh'
      }}>
        {/* Current Password */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5vh' }}>
          <label style={{
            fontSize: '1.5vh',
            fontFamily: 'Noto Sans',
            fontWeight: '600',
            color: 'rgba(255, 255, 255, 0.9)'
          }}>
            Current Password
          </label>
          <input
            type="password"
            name="currentPassword"
            value={formData.currentPassword}
            onChange={handleChange}
            style={{
              backgroundColor: 'rgba(60, 60, 60, 0.8)',
              border: `0.2vh solid rgba(90, 90, 90, 0.8)`,
              padding: '1vh',
              fontSize: '1.5vh',
              fontFamily: 'Noto Sans',
              borderRadius: '0.75vh',
              color: 'rgba(255, 255, 255, 0.9)',
              outline: 'none'
            }}
          />
        </div>

        {/* New Password */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5vh' }}>
          <label style={{
            fontSize: '1.5vh',
            fontFamily: 'Noto Sans',
            fontWeight: '600',
            color: 'rgba(255, 255, 255, 0.9)'
          }}>
            New Password
          </label>
          <input
            type="password"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            style={{
              backgroundColor: 'rgba(60, 60, 60, 0.8)',
              border: `0.2vh solid rgba(90, 90, 90, 0.8)`,
              padding: '1vh',
              fontSize: '1.5vh',
              fontFamily: 'Noto Sans',
              borderRadius: '0.75vh',
              color: 'rgba(255, 255, 255, 0.9)',
              outline: 'none'
            }}
          />
        </div>

        {/* Confirm Password */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5vh' }}>
          <label style={{
            fontSize: '1.5vh',
            fontFamily: 'Noto Sans',
            fontWeight: '600',
            color: 'rgba(255, 255, 255, 0.9)'
          }}>
            Confirm New Password
          </label>
          <input
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            style={{
              backgroundColor: 'rgba(60, 60, 60, 0.8)',
              border: `0.2vh solid rgba(90, 90, 90, 0.8)`,
              padding: '1vh',
              fontSize: '1.5vh',
              fontFamily: 'Noto Sans',
              borderRadius: '0.75vh',
              color: 'rgba(255, 255, 255, 0.9)',
              outline: 'none'
            }}
          />
        </div>

        {/* Password Requirements */}
        {formData.newPassword && (
          <div style={{
            backgroundColor: 'rgba(40, 40, 40, 0.6)',
            padding: '1.5vh',
            borderRadius: '0.75vh',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75vh'
          }}>
            <div style={{
              fontSize: '1.4vh',
              fontFamily: 'Noto Sans',
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '0.5vh'
            }}>
              Password Requirements:
            </div>
            <RequirementItem met={passwordRequirements.minLength} text="At least 8 characters" />
            <RequirementItem met={passwordRequirements.hasUppercase} text="One uppercase letter" />
            <RequirementItem met={passwordRequirements.hasLowercase} text="One lowercase letter" />
            <RequirementItem met={passwordRequirements.hasNumber} text="One number" />
            <RequirementItem met={passwordRequirements.hasSpecial} text="One special character" />
            {formData.confirmPassword && (
              <RequirementItem met={passwordsMatch} text="Passwords match" />
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: 'rgba(200, 60, 60, 0.2)',
            border: '0.2vh solid rgb(200, 60, 60)',
            padding: '1vh',
            borderRadius: '0.75vh',
            fontSize: '1.5vh',
            fontFamily: 'Noto Sans',
            color: 'rgb(255, 150, 150)',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div style={{
            backgroundColor: 'rgba(60, 200, 60, 0.2)',
            border: '0.2vh solid rgb(60, 200, 60)',
            padding: '1vh',
            borderRadius: '0.75vh',
            fontSize: '1.5vh',
            fontFamily: 'Noto Sans',
            color: 'rgb(150, 255, 150)',
            textAlign: 'center'
          }}>
            Password updated successfully!
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            backgroundColor: canSubmit ? `rgba(${uiData.schemeColor}, 0.2)` : 'rgba(60, 60, 60, 0.8)',
            border: canSubmit ? `0.2vh solid rgb(${uiData.schemeColor})` : '0.2vh solid rgba(90, 90, 90, 0.8)',
            padding: '1vh',
            fontSize: '1.5vh',
            fontFamily: 'Noto Sans',
            fontWeight: '600',
            borderRadius: '0.75vh',
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            transition: 'all 0.3s ease',
            color: 'rgba(255, 255, 255, 0.9)'
          }}
        >
          {loading ? 'Updating Password...' : 'Update Password'}
        </button>
      </form>
    </div>
  );
};

export default PasswordChangeForm;
